import { useTranslation } from 'react-i18next';
import type { CheckoutSubMode } from '../checkoutApi.js';
import {
  getCheckoutModeVisual,
  resolveCheckoutModeDetailI18nKey,
} from '../checkoutModePresentation.js';

function iconClass(channel: ReturnType<typeof getCheckoutModeVisual>['paymentChannel']): string {
  switch (channel) {
    case 'mobile':
      return 'bg-[var(--color-accent)]/15 text-[var(--color-accent)] ring-[var(--color-accent)]/30';
    case 'kiosk':
      return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)] ring-[var(--color-warning)]/35';
    case 'shop':
      return 'bg-[var(--color-success)]/15 text-[var(--color-success)] ring-[var(--color-success)]/30';
    default:
      return 'bg-[var(--color-surface)] text-[var(--color-on-surface)] ring-[var(--color-border)]';
  }
}

/** Read-only summary when phone-first journey locks checkout mode (plan §13). */
export function CheckoutModeSummaryCard({
  mode,
  selfCollectConfirmAtKiosk = false,
  collectTiming = null,
}: {
  mode: CheckoutSubMode;
  /** When false (default / fail-closed), use shop/app modeDetail onSite keys + shop visual chrome. */
  selfCollectConfirmAtKiosk?: boolean;
  /** Session collect timing — PREPAY + NOW uses collectNow copy (Buy Again coerce). */
  collectTiming?: 'NOW' | 'LATER' | null;
}): JSX.Element {
  const { t } = useTranslation('customer');
  const atKiosk = selfCollectConfirmAtKiosk === true;
  const visual = getCheckoutModeVisual(mode, { atKiosk });
  const titleKey = resolveCheckoutModeDetailI18nKey(mode, 'title', {
    atKiosk,
    collectTiming,
  });
  const descriptionKey = resolveCheckoutModeDetailI18nKey(mode, 'description', {
    atKiosk,
    collectTiming,
  });

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-elevated)] p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-on-surface-muted)]">
        {t('checkout.modeSummaryLabel')}
      </p>
      <div className="mt-2 flex items-start gap-3">
        <span
          aria-hidden="true"
          className={[
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold ring-1',
            iconClass(visual.paymentChannel),
          ].join(' ')}
        >
          {visual.iconLabel}
        </span>
        <div className="min-w-0">
          <p className="text-base font-semibold text-[var(--color-on-surface)]">
            {t(titleKey)}
          </p>
          <p className="mt-1 text-sm text-[var(--color-on-surface-muted)]">
            {t(descriptionKey)}
          </p>
        </div>
      </div>
    </div>
  );
}
